/**
 * プロジェクト→スキル自動同期サービス
 * プロジェクトのtechStackからスキルを自動抽出し、Skillsと同期する
 */

import type { Project } from '@/pages/Projects/Projects'
import type { AnalyzedSkill } from './ai/aiService'

const PROJECTS_STORAGE_KEY = 'portfolio-projects'
const SKILLS_STORAGE_KEY = 'portfolio-skills'

// 技術名 → カテゴリのマッピング
const techCategoryMap: Record<string, AnalyzedSkill['category']> = {
  // Frontend
  'React': 'Frontend',
  'Vue': 'Frontend',
  'Angular': 'Frontend',
  'Next.js': 'Frontend',
  'Svelte': 'Frontend',
  'Tailwind CSS': 'Frontend',
  'CSS': 'Frontend',
  'HTML': 'Frontend',
  'Sass': 'Frontend',
  'SCSS': 'Frontend',
  'Bootstrap': 'Frontend',

  // Backend
  'Node.js': 'Backend',
  'Express': 'Backend',
  'Django': 'Backend',
  'FastAPI': 'Backend',
  'Flask': 'Backend',
  'Spring': 'Backend',
  'NestJS': 'Backend',
  'GraphQL': 'Backend',
  'REST API': 'Backend',
  'PostgreSQL': 'Backend',
  'MySQL': 'Backend',
  'MongoDB': 'Backend',
  'Redis': 'Backend',
  'DynamoDB': 'Backend',
  'SNS': 'Backend',

  // Language
  'TypeScript': 'Language',
  'JavaScript': 'Language',
  'Python': 'Language',
  'Java': 'Language',
  'Go': 'Language',
  'Rust': 'Language',
  'C++': 'Language',
  'C#': 'Language',
  'Ruby': 'Language',
  'PHP': 'Language',
  'Kotlin': 'Language',
  'Swift': 'Language',

  // Infrastructure
  'AWS': 'Infrastructure',
  'AWS Lambda': 'Infrastructure',
  'GCP': 'Infrastructure',
  'Azure': 'Infrastructure',
  'Cloudflare': 'Infrastructure',
  'Workers AI': 'Infrastructure',
  'Docker': 'Infrastructure',
  'Kubernetes': 'Infrastructure',
  'Terraform': 'Infrastructure',
  'GitHub Actions': 'Infrastructure',
  'IaC': 'Infrastructure',
  'CI/CD': 'Infrastructure',
  'Vercel': 'Infrastructure',
  'Netlify': 'Infrastructure',

  // Other (AI, APIs, etc.)
  'Gemini AI': 'Other',
  'OpenAI': 'Other',
  'LINE API': 'Other',
  'LINE Bot': 'Other',
  'Google Places API': 'Other',
  'Slack API': 'Other',
}

/**
 * 技術名からカテゴリを判定
 */
function getCategoryForTech(tech: string): AnalyzedSkill['category'] {
  // 完全一致を確認
  if (techCategoryMap[tech]) {
    return techCategoryMap[tech]
  }

  // 部分一致を確認（大文字小文字を無視）
  const lowerTech = tech.toLowerCase()
  for (const [key, category] of Object.entries(techCategoryMap)) {
    if (lowerTech.includes(key.toLowerCase()) || key.toLowerCase().includes(lowerTech)) {
      return category
    }
  }

  // デフォルトはOther
  return 'Other'
}

/**
 * localStorageからプロジェクトを読み込む
 */
export function loadProjectsFromStorage(): Project[] {
  const saved = localStorage.getItem(PROJECTS_STORAGE_KEY)
  if (!saved) return []

  try {
    return JSON.parse(saved) as Project[]
  } catch {
    return []
  }
}

/**
 * localStorageからスキルを読み込む
 */
export function loadSkillsFromStorage(): AnalyzedSkill[] {
  const saved = localStorage.getItem(SKILLS_STORAGE_KEY)
  if (!saved) return []

  try {
    return JSON.parse(saved) as AnalyzedSkill[]
  } catch {
    return []
  }
}

/**
 * スキルをlocalStorageに保存
 */
export function saveSkillsToStorage(skills: AnalyzedSkill[]): void {
  localStorage.setItem(SKILLS_STORAGE_KEY, JSON.stringify(skills))
}

/**
 * プロジェクトからスキルを抽出
 */
export function extractSkillsFromProjects(projects: Project[]): AnalyzedSkill[] {
  // 全プロジェクトからtechStackを集計
  const techCount = new Map<string, number>()

  for (const project of projects) {
    for (const tech of project.techStack) {
      const normalizedTech = tech.trim()
      if (normalizedTech) {
        techCount.set(normalizedTech, (techCount.get(normalizedTech) || 0) + 1)
      }
    }
  }

  // スキルに変換（出現頻度に基づいてレベルを計算）
  const maxCount = Math.max(...Array.from(techCount.values()), 1)
  const skills: AnalyzedSkill[] = []

  for (const [tech, count] of techCount.entries()) {
    // レベルを50-95の範囲で計算（頻度が高いほど高レベル）
    const level = Math.min(95, Math.max(50, Math.round(50 + (count / maxCount) * 45)))

    skills.push({
      name: tech,
      level,
      category: getCategoryForTech(tech),
    })
  }

  // レベルの高い順にソート
  return skills.sort((a, b) => b.level - a.level)
}

/**
 * 既存スキルとプロジェクトから抽出したスキルをマージ
 * - 既存スキルのレベルを維持
 * - 新しいスキルを追加
 */
export function mergeSkills(
  existingSkills: AnalyzedSkill[],
  projectSkills: AnalyzedSkill[]
): AnalyzedSkill[] {
  const skillMap = new Map<string, AnalyzedSkill>()

  // 既存スキルを追加
  for (const skill of existingSkills) {
    skillMap.set(skill.name.toLowerCase(), skill)
  }

  // プロジェクトから抽出したスキルを追加（既存のものは上書きしない）
  for (const skill of projectSkills) {
    const key = skill.name.toLowerCase()
    if (!skillMap.has(key)) {
      skillMap.set(key, skill)
    }
  }

  // 配列に変換してレベル順にソート
  return Array.from(skillMap.values()).sort((a, b) => b.level - a.level)
}

/**
 * プロジェクトとスキルを同期
 * @returns 更新されたスキル配列
 */
export function syncProjectsToSkills(): AnalyzedSkill[] {
  const projects = loadProjectsFromStorage()
  const existingSkills = loadSkillsFromStorage()
  const projectSkills = extractSkillsFromProjects(projects)
  const mergedSkills = mergeSkills(existingSkills, projectSkills)

  saveSkillsToStorage(mergedSkills)
  return mergedSkills
}

/**
 * プロジェクト変更イベントをディスパッチ
 */
export function dispatchProjectsUpdated(): void {
  window.dispatchEvent(new CustomEvent('projectsUpdated'))
}

/**
 * プロジェクト変更イベントをリッスン
 */
export function onProjectsUpdated(callback: () => void): () => void {
  const handler = () => callback()
  window.addEventListener('projectsUpdated', handler)
  return () => window.removeEventListener('projectsUpdated', handler)
}
